import * as React from 'react';
import { Bike } from 'lucide-react';
import { CYCLE_TYPE_LABELS, CYCLE_TYPES, type CycleType } from '@hch/shared';
import {
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '~/components/ui/primitives';
import { useBicycles } from '~/lib/queries';
import { useAuth } from '~/lib/auth';
import { cn } from '~/lib/utils';
import type { BookingDraft } from '../booking-types';
import { StepShell } from '../step-shell';

/**
 * Tunnel de reservation — parcours principal de l'application.
 *
 * L'ordre des etapes suit litteralement l'enonce : adresse, cycle, operation de
 * maintenance, forfait, creneau, complements. Deux partis pris :
 *
 *   - La couverture est verifiee DES l'etape 1. Un client hors zone l'apprend
 *     immediatement, avant d'avoir saisi quoi que ce soit d'autre — plutot qu'apres
 *     cinq etapes, ce qui serait une perte de temps et une mauvaise experience.
 *
 *   - Le compte n'est jamais un prealable (RG-04). Un visiteur connecte se voit
 *     simplement proposer ses adresses et ses cycles enregistres.
 */

export function BicycleStep({
  draft,
  patch,
  onBack,
  onNext,
}: {
  draft: BookingDraft;
  patch: (values: Partial<BookingDraft>) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const { isAuthenticated } = useAuth();
  const { data: savedBicycles } = useBicycles(isAuthenticated);

  const currentYear = new Date().getFullYear();
  const [form, setForm] = React.useState({
    brand: draft.newBicycle?.brand ?? '',
    model: draft.newBicycle?.model ?? '',
    manufacturedYear: draft.newBicycle?.manufacturedYear ?? currentYear,
    type: (draft.newBicycle?.type ?? 'BIKE') as CycleType,
  });

  function useNewBicycle() {
    patch({
      bicycleId: undefined,
      newBicycle: form,
      cycleType: form.type,
      bicycleLabel: `${form.brand} ${form.model} (${form.manufacturedYear})`,
    });
  }

  // La validite est evaluee sur la SAISIE en cours, pas sur le brouillon : ce dernier
  // n'est alimente qu'au clic sur « Continuer », si bien qu'en s'y fiant le bouton
  // resterait desactive indefiniment pour un nouveau cycle.
  const newBicycleValid = form.brand.trim().length > 0 && form.model.trim().length > 0;
  const ready = !!draft.bicycleId || newBicycleValid;

  return (
    <StepShell
      title="Quel cycle devons-nous prendre en charge ?"
      description="Le modele et l'annee nous permettent d'emporter les bonnes pieces."
      onBack={onBack}
      onNext={() => {
        if (!draft.bicycleId) useNewBicycle();
        onNext();
      }}
      nextDisabled={!ready}
    >
      {isAuthenticated && savedBicycles && savedBicycles.length > 0 ? (
        <div className="space-y-2">
          <p className="app-section-title">Mes cycles</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {savedBicycles.map((bicycle) => (
              <button
                key={bicycle.id}
                type="button"
                onClick={() =>
                  patch({
                    bicycleId: bicycle.id,
                    newBicycle: undefined,
                    cycleType: bicycle.type,
                    bicycleLabel: `${bicycle.brand} ${bicycle.model}`,
                  })
                }
                className={cn(
                  'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent',
                  draft.bicycleId === bicycle.id ? 'border-primary bg-accent' : 'border-border',
                )}
              >
                <Bike className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">
                    {bicycle.brand} {bicycle.model}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {bicycle.manufacturedYear} · {CYCLE_TYPE_LABELS[bicycle.type]}
                  </span>
                </span>
              </button>
            ))}
          </div>
          <Separator className="my-2" />
          <p className="app-section-title">Ou ajouter un nouveau cycle</p>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Marque" htmlFor="brand" required>
          <Input
            value={form.brand}
            onChange={(event) => setForm({ ...form, brand: event.target.value })}
            placeholder="Btwin, Giant, Moustache…"
          />
        </Field>

        <Field label="Modele" htmlFor="model" required>
          <Input
            value={form.model}
            onChange={(event) => setForm({ ...form, model: event.target.value })}
            placeholder="Elops 520"
          />
        </Field>

        <Field label="Annee" htmlFor="year" required>
          <Input
            type="number"
            min={1950}
            max={currentYear + 1}
            value={form.manufacturedYear}
            onChange={(event) =>
              setForm({ ...form, manufacturedYear: Number(event.target.value) || currentYear })
            }
          />
        </Field>

        <Field label="Type" htmlFor="type" required>
          <Select
            value={form.type}
            onValueChange={(value) => setForm({ ...form, type: value as CycleType })}
          >
            <SelectTrigger id="type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CYCLE_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {CYCLE_TYPE_LABELS[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
    </StepShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Etape 3 — Operation de maintenance (RG-01)
// ═══════════════════════════════════════════════════════════════════════════════
